import { generateObsSceneCollection, obsFileStem, type ObsStreamInput } from '@/lib/obs'
import { createStoredZip } from '@/lib/obsZip'

export interface ObsSetup {
  eventId: string
  eventName: string
  streams: ObsStreamInput[]
  returnUrl?: string
}

export function generateObsSetupFiles({ eventId, eventName, streams, returnUrl }: ObsSetup): Record<string, string> {
  const destination = returnUrl === undefined ? undefined : new URL(returnUrl)
  if (destination && (destination.protocol !== 'srt:' || !destination.searchParams.get('streamid'))) {
    throw new Error('The live show address is not ready. Try again.')
  }
  const name = `${eventName.replace(/[\r\n\[\]]/g, ' ').trim()} - StreamBridge`
  const profile = `streambridge-${obsFileStem(eventName)}-${obsFileStem(eventId)}`
  const inputs = streams.filter((stream) => {
    if (stream.sourceRole === 'programme' || !stream.egressUrl) return false
    const input = new URL(stream.egressUrl)
    return !destination || input.searchParams.get('streamid') !== destination.searchParams.get('streamid')
  })

  if (!destination) {
    return {
      'scenes.json': JSON.stringify(generateObsSceneCollection(eventName, inputs), null, 2),
      'START-HERE.txt': `STREAMBRIDGE · ${eventName}

1. Extract this ZIP.
2. In OBS, use Scene Collection > Import and select scenes.json. Select the imported collection.
3. Choose a camera scene and check its picture and audio.
4. Use your OBS output settings to record or stream.

This download includes ${inputs.length} camera(s). Cameras can connect later. Download again after adding cameras.
Each camera scene uses that camera's audio. The All cameras grid is silent. Desktop and microphone capture are not added.
Keep existing customized collections under separate names.

Optional: to send your finished OBS mix back to this event, choose Add live show in StreamBridge, then download the updated OBS setup.

Keep this package private: it includes camera connection credentials.
`,
    }
  }

  return {
    'scenes.json': JSON.stringify(generateObsSceneCollection(eventName, inputs), null, 2),
    [`${profile}/basic.ini`]: `[General]
Name=${name}

[Video]
BaseCX=1920
BaseCY=1080
OutputCX=1280
OutputCY=720
FPSType=0
FPSCommon=30
ScaleType=bicubic
ColorFormat=NV12
ColorSpace=709
ColorRange=Partial

[Audio]
SampleRate=48000
ChannelSetup=Stereo

[Output]
Mode=Simple
Reconnect=true
RetryDelay=2
MaxRetries=30
DelayEnable=false

[SimpleOutput]
StreamEncoder=x264
StreamAudioEncoder=aac
VBitrate=3500
ABitrate=160
UseAdvanced=true
Preset=veryfast
x264Settings=keyint=60 bframes=0
RecQuality=Stream
RecFormat2=mkv

[Stream1]
IgnoreRecommended=true
`,
    [`${profile}/service.json`]: JSON.stringify({
      type: 'rtmp_custom',
      settings: { server: returnUrl, key: '', use_auth: false },
    }, null, 2),
    'START-HERE.txt': `STREAMBRIDGE · ${eventName}

1. Extract this ZIP.
2. In OBS, use Scene Collection > Import and select scenes.json. Select the imported collection.
3. Use Profile > Import and select the ${profile} folder. Select the imported profile.
4. Choose a camera scene, check its picture and audio, then select Start Streaming.
5. Return to your event workspace. Live show appears when its video arrives.

This download includes ${inputs.length} camera(s). Cameras can connect later. Download again after adding cameras.
Each camera scene uses that camera's audio. The All cameras grid is silent. Desktop and microphone capture are not added.
The profile sends 720p30 H.264/AAC to this event. You can change quality in OBS after checking your connection.
OBS uses two separate imports: scene collections hold cameras; profiles hold the streaming destination.
Only select this event's matching collection and profile. Keep existing customized collections and profiles under separate names.
Reimporting a profile with an existing folder name requires removing or renaming the previous profile in OBS first.

Manual destination: Settings > Stream > Custom. Paste the SRT address from the event's OBS panel into Server; leave Stream Key empty.
Live show is deliberately excluded from its own camera collection to prevent a video/audio feedback loop.

Keep this package private: it includes camera and show connection credentials.
`,
  }
}

export function downloadObsSetup(setup: ObsSetup): void {
  const bytes = createStoredZip(generateObsSetupFiles(setup))
  const blob = new Blob([bytes], { type: 'application/zip' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${obsFileStem(setup.eventName)}-obs-setup.zip`
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
